import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthenticatorService } from '@aws-amplify/ui-angular';

import { Router, ActivatedRoute } from '@angular/router';
import { LocalStorageService } from '../localStorage/local-storage.service';
import { GLOBAL } from 'src/app/_config/config';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Authorizations } from 'src/app/interfaces/auth.interfaces';


/**
 * Servicio central de autenticación y autorización.
 *
 * Gestiona el usuario actual y el JWT mediante BehaviorSubject sincronizados
 * con `localStorage`, y expone utilidades para validar permisos por perfil
 * (módulo → menú → opción), consultar el estado de la sesión y cerrar sesión.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public sessionDate: Date = new Date();
  idToken: string = '';
  private isAuthorized: boolean = false;
  public currentUser: Observable<any>;
	private currentUserSubject: BehaviorSubject<any>;

  public JWT: Observable<any>;
  private JWTSubject: BehaviorSubject<any>;


  constructor(
    public authenticator: AuthenticatorService,
    private router : Router,
    private localStorageServices: LocalStorageService,
    private http: HttpClient
  ) {
    this.currentUserSubject = new BehaviorSubject<any>( JSON.parse(localStorage.getItem('currentUser')!) );
		this.currentUser = this.currentUserSubject.asObservable();

    this.JWTSubject = new BehaviorSubject<any>( JSON.parse(localStorage.getItem('JWT')!) );
    this.JWT = this.JWTSubject.asObservable();
  }

  /**
   * Valor actual del usuario autenticado almacenado en el BehaviorSubject.
   * @returns Objeto del usuario actual o `null` si no hay sesión.
   */
  public get currentUserValue(): any {
    return this.currentUserSubject.value;
  }
  /**
   * Actualiza el usuario actual en `localStorage` y notifica a los suscriptores.
   * @param user - Objeto del usuario a persistir y emitir.
   */
  public updateUserValue(user:any) {
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  /**
   * Valor actual del JWT almacenado en el BehaviorSubject.
   * @returns Token JWT actual o `null` si no existe.
   */
  public get JWTValue(): any {
    return this.JWTSubject.value;
  }

  /**
   * Indica si el usuario está marcado temporalmente como no autorizado.
   * @returns `true` cuando se detectó un acceso no permitido recientemente.
   */
  public get isUserAuthorized(): boolean {
    return this.isAuthorized;
  }

  /**
   * Obtiene las autorizaciones del usuario desde el backend.
   * @returns Observable con la respuesta de autorizaciones del servidor.
   */
  getAuthorizations(): Observable<any> {
    const url: string = `${GLOBAL.url}${GLOBAL.authorizations.get}`;
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': this.idToken
    });
    return this.http.get(url, { headers });
  }

  /**
   * Recupera el objeto de autorizaciones persistido en `localStorage`.
   * @returns Objeto de autorizaciones almacenado o cadena vacía si no existe.
   */
  getAuthorizationsObj(): any {
    return this.localStorageServices.getData('authorizations');
  }

  /**
   * Obtiene el token JWT del usuario actual almacenado localmente.
   * @returns Token JWT del usuario o cadena vacía si no está disponible.
   */
  getToken() {
    const user = this.localStorageServices.getData('currentUser');
    return user.jwtToken || '';
  }

  /**
   * Obtiene las acciones autorizadas para un módulo específico.
   * @param moduleName - Nombre del módulo a consultar.
   * @returns Objeto con las acciones del módulo o `null` si no existe.
   */
  getAuthorizationsByModule(moduleName: string) {
    const auth = this.getAuthorizationsObj();
    if (!auth || !auth.list_actions || !Array.isArray(auth.list_actions)) {
      return null;
    }
    const moduleIndex = auth.list_actions.findIndex( (module: { [x: string]: any; }) => module[moduleName]);
    if (moduleIndex === -1) {
      return null;
    }
    return auth.list_actions[moduleIndex][moduleName];
  }

  /**
   * Valida si la ruta es accesible; si no lo es, activa temporalmente la
   * bandera de acceso no autorizado durante 3 segundos.
   * @param module - Clave del módulo.
   * @param menu - Clave del menú.
   * @param opcion - Acción a validar.
   * @returns `true` si la ruta es válida, `false` en caso contrario.
   */
  isValidPath(module: string, menu: string, opcion: string) {
    const auth = this.getAuthorizationsByModule(module);
    if (!auth || !auth[menu] || auth[menu][opcion] === false) {
      this.isAuthorized = true;
      setTimeout(() => {
        this.isAuthorized = false;
      }, 3000);
      return false;
    }
    return true;
  }

  /**
   * Valida si el perfil tiene permiso sobre módulo → menú → opción.
   * @param module - Clave del módulo.
   * @param menu - Clave del menú.
   * @param opcion - Acción a validar.
   * @returns `true` si el acceso está permitido.
   */
  hasAuthorization(module: string, menu: string, opcion: string) {
    const auth = this.getAuthorizationsByModule(module);
    if (!auth || !auth[menu] || auth[menu][opcion] === false) {
      return false;
    }
    return true;
  }

  /**
   * Indica si existe un usuario con sesión iniciada.
   * @returns `true` si hay un usuario actual, `false` en caso contrario.
   */
  isLogged() {
    if(this.currentUserValue)
      return true;
    return false;
  }

  /**
   * Cierra la sesión del usuario: revoca la sesión de Amplify, limpia
   * `localStorage`, reinicia los observables y redirige al login.
   */
  logout() {
    this.authenticator.signOut();
    localStorage.removeItem('currentUser');
    localStorage.removeItem('JWT');
    localStorage.clear();
    this.currentUserSubject.next(null);
    this.JWTSubject.next(null);
    this.router.navigate(['login']);
  }

  /**
   * Determina si la sesión del usuario sigue vigente según su expiración.
   * @returns `true` si la fecha de expiración del token es futura.
   */
  isSessionActive(): boolean {
    const user = this.localStorageServices.getData('currentUser');
    if (user.exp) {
      const expDate = new Date(user.exp * 1000);
      return expDate > new Date();
    }
    return false;
  }


}
